import {
  asyncScheduler,
  buffer,
  connect,
  filter,
  ignoreElements,
  map,
  merge,
  pipe,
  share,
  throttleTime,
  type Observable,
  type UnaryFunction,
} from 'rxjs';
import type { MinimalSubscriptionResponse } from '../types/SubscriptionResponse';

// Throttle + Buffer combined
// https://stackoverflow.com/questions/66913467/in-rxjs-how-can-you-make-a-throttled-buffer-operator
export function throttledBuffer<T>(throttleTimeMs: number): UnaryFunction<Observable<T>, Observable<T[]>> {
  return pipe(
    connect(observable$ => {
      const throttleCalls$ = observable$.pipe(
        throttleTime(throttleTimeMs, asyncScheduler, {
          leading: true,
          trailing: true,
        })
      );

      const src$ = observable$.pipe(share());
      return merge(src$.pipe(ignoreElements()), src$.pipe(buffer(throttleCalls$)));
    })
  );
}

/**
 * Connects to the throttledBuffer
 *
 * Given a buffer array and a way to determine an item's uniqueness, will coalesce the array of provided SubscriptionResponse messages
 * into just one output message.
 *
 * Note: Does _not_ handle deletion messages. Only works on additive data streams such as Orders.
 */
export function combineBufferedSubscriptionResponses<T>(
  buffer: MinimalSubscriptionResponse<T>[],
  getUniqueKey: (item: T) => string
): MinimalSubscriptionResponse<T> | null {
  if (buffer.length === 0) {
    // this null value is then caught by an rxjs filter later down the line and ignored
    return null;
  }

  if (buffer.length === 1) {
    return buffer[0];
  }

  const uniqueUpdates = new Map<string, T>();
  let metInitialMessage = false;
  for (const update of buffer) {
    if (update.initial) {
      // If we run into an initial: true message while processing our buffer, then we clear everything that came before it.
      uniqueUpdates.clear();
      // We also have to remember that we've ran into an initial message, since this information needs to be conveyed in our final output message as well.
      metInitialMessage = true;
    }

    for (const datapoint of update.data) {
      uniqueUpdates.set(getUniqueKey(datapoint), datapoint);
    }
  }

  // use the metadata of this last msg. data will be overridden, as well as the initial property
  const lastJson = buffer[buffer.length - 1];

  return {
    ...lastJson,
    initial: metInitialMessage,
    data: [...uniqueUpdates.values()],
  };
}

/**
 * An RxJS pipe which throttles received SubscriptionResponse messages for throttleTimeMs milliseconds and then
 * emits one single MinimalSubscriptionResponse<T> message with unique updates in the data array.
 * The pipe emits immediately on first message received, and thereafter applies the buffering delay.
 * If there are conflicting messages in the received SubscriptionResponse's data array, the last one received is used.
 * @param throttleTimeMs the time in ms to buffer for
 * @param getUniqueKey get a unique key for each item in order to resolve conflicts
 */
export function throttledSubscriptionResponse<T>(throttleTimeMs: number, getUniqueKey: (item: T) => string) {
  return pipe(
    throttledBuffer<MinimalSubscriptionResponse<T>>(throttleTimeMs),
    map(buffer => combineBufferedSubscriptionResponses(buffer, getUniqueKey)),
    filter((message): message is MinimalSubscriptionResponse<T> => message != null)
  );
}
